
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Building2, Users, Settings } from "lucide-react";
import { CSOLoadingSpinner } from "@/components/ui/cso-loading-spinner";

export const OrganizationAccess = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [subscription, setSubscription] = useState<any>(null);
  const [organizationMembership, setOrganizationMembership] = useState<any>(null);

  useEffect(() => {
    const checkOrganizationAccess = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) return;

        console.log('Checking organization access for user:', session.user.id);

        // Check user's subscription type
        const { data: subData, error: subError } = await supabase
          .from('user_subscriptions')
          .select('*')
          .eq('user_id', session.user.id)
          .eq('status', 'active')
          .maybeSingle();

        if (subError) {
          console.error('Error fetching subscription:', subError);
        }

        console.log('User subscription:', subData);
        setSubscription(subData);

        // Check if user is part of an organization
        const { data: orgMembership, error: orgError } = await supabase
          .from('organization_memberships')
          .select(`
            *,
            organizations (
              id,
              name,
              tier
            )
          `)
          .eq('user_id', session.user.id)
          .maybeSingle();

        if (orgError) {
          console.error('Error fetching organization membership:', orgError);
        } else {
          console.log('Organization membership:', orgMembership);
        }

        setOrganizationMembership(orgMembership);
      } catch (error) {
        console.error('Error checking organization access:', error);
      } finally {
        setIsLoading(false);
      }
    };

    checkOrganizationAccess();
  }, []);

  const handleOrganizationDashboard = () => {
    navigate('/organization/dashboard');
  };

  const handleCreateOrganization = () => {
    navigate('/organization/signup');
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex justify-center">
            <CSOLoadingSpinner size="sm" />
          </div>
        </CardContent>
      </Card>
    );
  }

  // Show organization access only for team/enterprise plans
  const isTeamOrEnterprise = subscription?.subscription_type === 'team' || subscription?.subscription_type === 'enterprise';

  if (!isTeamOrEnterprise) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Building2 className="h-5 w-5" />
          Organization Management
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {organizationMembership?.organizations ? (
          <div className="space-y-4">
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center gap-3 mb-2">
                <Users className="h-5 w-5 text-blue-600" />
                <span className="font-medium text-blue-800">
                  {organizationMembership.organizations.name}
                </span>
              </div>
              <p className="text-sm text-blue-600 mb-3">
                Role: {organizationMembership.role} • Plan: {organizationMembership.organizations.tier}
              </p>
              <Button
                onClick={handleOrganizationDashboard}
                className="w-full bg-blue-600 hover:bg-blue-700"
              >
                <Settings className="h-4 w-4 mr-2" />
                Access Organization Dashboard
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center gap-3 mb-2">
                <Building2 className="h-5 w-5 text-green-600" />
                <span className="font-medium text-green-800">
                  Create Your Organization
                </span>
              </div>
              <p className="text-sm text-green-600 mb-3">
                Set up your organization to invite team members and manage collaboration.
              </p>
              <Button
                onClick={handleCreateOrganization}
                className="w-full bg-green-600 hover:bg-green-700"
              >
                <Users className="h-4 w-4 mr-2" />
                Create Organization
              </Button>
            </div>
          </div>
        )}
        
        <div className="text-xs text-gray-500">
          Organization features are available for Team and Enterprise plans.
        </div>
      </CardContent>
    </Card>
  );
};
